import { useEffect, useState } from "react";
import { staffApi, ApiError } from "@/api";
import type { LineItem, LineItemUpdate, Currency, QuoteLineDrawingType } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import "./line-items.css";

/** The three selectable states a line item can take (spec §7), collapsing the
 *  two booleans (is_optional + default_selected) into one control. */
type Status = "required" | "optional_on" | "optional_off";

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "required", label: "Required" },
  { value: "optional_on", label: "Optional — selected by default" },
  { value: "optional_off", label: "Optional — off by default" },
];

/** Drawing this line seeds at provisioning (§8.2). "none" = no drawing. */
const DRAWING_TYPE_OPTIONS: { value: QuoteLineDrawingType; label: string }[] = [
  { value: "none", label: "No drawing" },
  { value: "planning", label: "Planning" },
  { value: "building_control", label: "Building control" },
  { value: "other", label: "Other" },
];

function statusOf(item: Pick<LineItem, "is_optional" | "default_selected">): Status {
  if (!item.is_optional) return "required";
  return item.default_selected ? "optional_on" : "optional_off";
}

function statusFields(status: Status): { is_optional: boolean; default_selected: boolean } {
  switch (status) {
    case "required":
      return { is_optional: false, default_selected: false };
    case "optional_on":
      return { is_optional: true, default_selected: true };
    case "optional_off":
      return { is_optional: true, default_selected: false };
  }
}

/** One bullet per line, both ways — empties trimmed out. */
const linesToArray = (text: string): string[] =>
  text.split("\n").map((s) => s.trim()).filter(Boolean);
const arrayToLines = (arr: string[] | null | undefined): string => (arr ?? []).join("\n");

interface DraftItem {
  title: string;
  description: string;
  quantity: string;
  unit_price: string;
  status: Status;
  included: string;
  not_included: string;
  drawing_type: QuoteLineDrawingType;
}

const EMPTY: DraftItem = {
  title: "",
  description: "",
  quantity: "1",
  unit_price: "0",
  status: "required",
  included: "",
  not_included: "",
  drawing_type: "none",
};

/** Line-item CRUD against endpoints 194/195/196. After each mutation we ask
 *  the parent to reload (GET quote/{id}) so the authoritative line_total and
 *  the quote_value headline stay correct — never recomputed client-side. */
export function LineItemsEditor({
  quoteId,
  items,
  currency = "GBP",
  disabled,
  onChanged,
}: {
  quoteId: number;
  items: LineItem[];
  currency?: Currency;
  disabled?: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<DraftItem>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const sorted = [...items].sort((a, b) => a.position - b.position);

  async function addItem() {
    const qty = Number(draft.quantity) || 1;
    const unit = Number(draft.unit_price) || 0;
    if (!draft.title.trim()) {
      setError("Give the line item a title.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const { is_optional, default_selected } = statusFields(draft.status);
      await staffApi.addLineItem(quoteId, {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        quantity: qty,
        unit_price: unit,
        line_total: qty * unit,
        is_optional,
        default_selected,
        included: linesToArray(draft.included),
        not_included: linesToArray(draft.not_included),
        drawing_type: draft.drawing_type,
        position: sorted.length,
      });
      setDraft(EMPTY);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the line item.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="line-items">
      {error ? <p className="line-items__error" role="alert">{error}</p> : null}

      {sorted.length === 0 ? (
        <p className="line-items__empty">No line items yet.</p>
      ) : (
        <ul className="line-items__list">
          {sorted.map((item) => (
            <LineItemRow
              key={item.id}
              item={item}
              currency={currency}
              disabled={disabled}
              onChanged={onChanged}
              onError={setError}
            />
          ))}
        </ul>
      )}

      {!disabled ? (
        <div className="line-items__add">
          <h3>Add a line item</h3>
          <TextField
            label="Title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
          <TextAreaField
            label="Description"
            rows={3}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <div className="line-items__add-row">
            <TextField
              label="Qty"
              type="number"
              min={0}
              value={draft.quantity}
              onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
            />
            <TextField
              label={`Unit price (${currency})`}
              type="number"
              min={0}
              step="0.01"
              value={draft.unit_price}
              onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
            />
          </div>
          <SelectField
            label="Status"
            hint="The client toggles optional items on the quote. Default-on items start ticked and count in the quote's headline value; other optional items are excluded until ticked."
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as Status })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <SelectField
            label="Seeds drawing"
            hint="When the accepted quote is provisioned, lines tagged here seed a drawing of this type into the job's new set."
            value={draft.drawing_type}
            onChange={(e) => setDraft({ ...draft, drawing_type: e.target.value as QuoteLineDrawingType })}
          >
            {DRAWING_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <div className="line-items__add-row">
            <TextAreaField
              label="Included (one per line)"
              rows={3}
              value={draft.included}
              onChange={(e) => setDraft({ ...draft, included: e.target.value })}
            />
            <TextAreaField
              label="Not included (one per line)"
              rows={3}
              value={draft.not_included}
              onChange={(e) => setDraft({ ...draft, not_included: e.target.value })}
            />
          </div>
          <Button variant="secondary" onClick={addItem} loading={busy}>Add line item</Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A single line item. When the quote is editable, its title/description/
 * unit_price/status/included/not-included are editable in place with a live
 * line_total preview; Save PATCHes only the changed text fields (never
 * line_total) and triggers a quote re-fetch. Changing the status PATCHes
 * immediately (it shifts the base total). When the quote is terminal
 * (disabled), it renders read-only.
 */
function LineItemRow({
  item,
  currency,
  disabled,
  onChanged,
  onError,
}: {
  item: LineItem;
  currency: Currency;
  disabled?: boolean;
  onChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? "");
  const [unitPrice, setUnitPrice] = useState(String(item.unit_price));
  const [included, setIncluded] = useState(arrayToLines(item.included));
  const [notIncluded, setNotIncluded] = useState(arrayToLines(item.not_included));
  const [busy, setBusy] = useState(false);

  // Re-sync local fields whenever the underlying item changes (e.g. after a
  // save-triggered reload brings back the server-derived line_total).
  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description ?? "");
    setUnitPrice(String(item.unit_price));
    setIncluded(arrayToLines(item.included));
    setNotIncluded(arrayToLines(item.not_included));
  }, [item.id, item.title, item.description, item.unit_price, item.included, item.not_included]);

  const unitNum = Number(unitPrice);
  const unitValid = unitPrice.trim() !== "" && Number.isFinite(unitNum) && unitNum >= 0;
  // Flat-fee model: line_total = unit_price (server forces quantity to 1).
  // Server value is authoritative on save.
  const previewTotal = unitValid ? unitNum : 0;

  const titleChanged = title !== item.title;
  const descChanged = description !== (item.description ?? "");
  const unitChanged = unitValid && unitNum !== item.unit_price;
  const includedChanged = included !== arrayToLines(item.included);
  const notIncludedChanged = notIncluded !== arrayToLines(item.not_included);
  const dirty = titleChanged || descChanged || unitChanged || includedChanged || notIncludedChanged;
  const canSave = dirty && title.trim() !== "" && unitValid;

  async function changeStatus(next: Status) {
    setBusy(true);
    try {
      await staffApi.updateLineItem(item.id, { patch: statusFields(next) });
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the line item.");
    } finally {
      setBusy(false);
    }
  }

  // drawing_type doesn't shift the total, but it's a one-field choice — patch it
  // immediately like the status control rather than gating behind Save.
  async function changeDrawingType(next: QuoteLineDrawingType) {
    setBusy(true);
    try {
      await staffApi.updateLineItem(item.id, { patch: { drawing_type: next } });
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the line item.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await staffApi.deleteLineItem(item.id);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the line item.");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!title.trim()) {
      onError("Give the line item a title.");
      return;
    }
    // Send ONLY changed fields, wrapped in a { patch } envelope — never quantity
    // or line_total. The server gates on the patch keys, so sending [] for a
    // cleared list is honoured.
    const patch: LineItemUpdate["patch"] = {};
    if (titleChanged) patch.title = title.trim();
    if (descChanged) patch.description = description.trim();
    if (unitChanged) patch.unit_price = unitNum;
    if (includedChanged) patch.included = linesToArray(included);
    if (notIncludedChanged) patch.not_included = linesToArray(notIncluded);
    if (Object.keys(patch).length === 0) return;

    setBusy(true);
    try {
      await staffApi.updateLineItem(item.id, { patch });
      // Re-fetch the quote: refreshes this row's authoritative line_total AND
      // the quote_value headline (which the server recomputed).
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the line item.");
    } finally {
      setBusy(false);
    }
  }

  const rowClass = `line-items__row${item.is_optional ? " line-items__row--optional" : ""}`;
  const statusLabel = STATUS_OPTIONS.find((o) => o.value === statusOf(item))?.label ?? "Required";
  const includedList = (item.included ?? []).filter((s) => s && s.trim());
  const notIncludedList = (item.not_included ?? []).filter((s) => s && s.trim());

  // Terminal quote → read-only view.
  if (disabled) {
    return (
      <li className={rowClass}>
        <div className="line-items__main">
          <span className="line-items__title">
            {item.title}
            {item.is_optional ? <span className="line-items__badge">{statusLabel}</span> : null}
          </span>
          {item.description ? <span className="line-items__desc">{item.description}</span> : null}
          {includedList.length > 0 ? (
            <SubList label="Included" items={includedList} />
          ) : null}
          {notIncludedList.length > 0 ? (
            <SubList label="Not included" items={notIncludedList} />
          ) : null}
          <span className="line-items__calc numeric">
            Line total: <strong>{formatMoney(item.line_total, currency)}</strong>
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className={rowClass}>
      <div className="line-items__edit">
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextAreaField
          label="Description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          label={`Unit price (${currency})`}
          type="number"
          min={0}
          step="0.01"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
        />
        <SelectField
          label="Status"
          value={statusOf(item)}
          disabled={busy}
          onChange={(e) => changeStatus(e.target.value as Status)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </SelectField>
        <SelectField
          label="Seeds drawing"
          value={item.drawing_type ?? "none"}
          disabled={busy}
          onChange={(e) => changeDrawingType(e.target.value as QuoteLineDrawingType)}
        >
          {DRAWING_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </SelectField>
        <div className="line-items__add-row">
          <TextAreaField
            label="Included (one per line)"
            rows={3}
            value={included}
            onChange={(e) => setIncluded(e.target.value)}
          />
          <TextAreaField
            label="Not included (one per line)"
            rows={3}
            value={notIncluded}
            onChange={(e) => setNotIncluded(e.target.value)}
          />
        </div>
        <div className="line-items__preview numeric">
          <span>
            Line total: <strong>{formatMoney(previewTotal, currency)}</strong>
          </span>
          {dirty ? <em className="line-items__preview-flag">unsaved</em> : null}
        </div>
      </div>
      <div className="line-items__row-actions">
        <Button variant="primary" size="sm" onClick={save} loading={busy} disabled={!canSave}>
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={remove} disabled={busy}>Delete</Button>
      </div>
    </li>
  );
}

function SubList({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="line-items__sub">
      <span className="line-items__sub-label">{label}</span>
      <ul className="line-items__sublist">
        {items.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ul>
    </div>
  );
}
